// The place an address names in its fragment (an item's `#~marzod`, a
// message's `#message-123`, `#open-questions`), kept through sign-in
// (FR-LOGIN-09). A browser never sends the fragment to the server, so a
// signed-out page load reaches the sign-in page with its path alone, and the
// browser keeps the fragment on the sign-in page's own address. This keeps
// it in the tab's session storage, with where signing in returns, for as
// long as a pending sign-in lasts; when that page then loads with no
// fragment, it takes the fragment back, which only scrolls to what it names
// and marks it (`:target`), as following the link would have. The fragment
// never rides in `return_to`, which goes to GitHub and back inside the OAuth
// state. Without JavaScript the page opens at its top, as it did.

/** The sign-in page's address (`auth_routes::LoginPath`). */
const SIGN_IN = "/-/login";

/** Where the fragment waits out the sign-in, for this tab. */
const KEY = "specter-sign-in-place";

/** As long as a pending sign-in lasts (`PENDING_SIGN_IN_SECS`). */
const KEEP_MS = 10 * 60 * 1000;

interface Kept {
  /** Where signing in returns, without a fragment. */
  to: string;
  /** The fragment, `#` included. */
  hash: string;
  /** When it was kept, in ms since the epoch. */
  at: number;
}

/** `address`, on this site, without its fragment; `null` elsewhere. */
function withoutFragment(address: string): string | null {
  try {
    const url = new URL(address, window.location.href);
    if (url.origin !== window.location.origin) return null;
    url.hash = "";
    return url.href;
  } catch {
    return null;
  }
}

/** On the sign-in page: keep its fragment with where it returns. */
function keep(): void {
  if (window.location.hash.length < 2) return;
  const params = new URLSearchParams(window.location.search);
  const to = withoutFragment(params.get("return_to") ?? "/");
  if (to === null) return;
  const kept: Kept = { to, hash: window.location.hash, at: Date.now() };
  try {
    sessionStorage.setItem(KEY, JSON.stringify(kept));
  } catch {
    // No storage here: the page opens at its top, as it did.
  }
}

/** What `keep` kept, while a sign-in could still be returning to it. */
function kept(): Kept | null {
  let stored: string | null;
  try {
    stored = sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
  if (stored === null) return null;
  try {
    const k = JSON.parse(stored) as Partial<Kept>;
    const fresh =
      typeof k.at === "number" &&
      Date.now() - k.at >= 0 &&
      Date.now() - k.at <= KEEP_MS;
    if (
      fresh &&
      typeof k.to === "string" &&
      typeof k.hash === "string" &&
      k.hash.startsWith("#")
    ) {
      return { to: k.to, hash: k.hash, at: k.at as number };
    }
  } catch {
    // Unreadable: forgotten below.
  }
  forget();
  return null;
}

function forget(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing kept, then.
  }
}

/**
 * At the page signing in returned to: forget the fragment, and put it back
 * when the page has none of its own. Only the fragment changes, so the
 * page isn't loaded again.
 */
function restore(): void {
  const k = kept();
  if (k === null || withoutFragment(window.location.href) !== k.to) return;
  forget();
  if (window.location.hash.length < 2) window.location.replace(k.to + k.hash);
}

if (window.location.pathname === SIGN_IN) keep();
else restore();
